import * as THREE from 'three';
import { computeDistanceAndSlope, HEMISPHERE_RADIUS_FT, hemisphereRadiusScene, isNearVertical } from './nearbyPoints';

/** Green "VERTICAL" header on the vertical-measurement label (#68FF54). */
const VERTICAL_HEADER_COLOR = { r: 104, g: 255, b: 84 };

function cssRgba(c: { r: number; g: number; b: number; a?: number }) {
    return `rgba(${c.r},${c.g},${c.b},${c.a ?? 1})`;
}

function roundRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

// Render a Potree TextSprite as two centred lines in one rounded box: a small
// coloured header (green "VERTICAL") above the value line. Mirrors Potree's
// TextSprite.update canvas work and is installed per-instance, so the colour /
// border setters (which call update) redraw through it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawTwoLineLabel(label: any) {
    const fontface = label.fontface ?? 'Arial';
    const valueSize = label.fontsize ?? 16;
    const headerSize = Math.round(valueSize * 0.75);
    const bt = label.borderThickness ?? 2;
    const margin = 6;
    const gap = 6;
    const header = String(label._verticalHeader ?? '');
    const value = String(label.text ?? '');
    // A slope-only vertical label carries just "VERTICAL" (no value line).
    const hasValue = value.length > 0;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Measure each line with real font metrics so the box hugs the glyphs — no
    // guessed line-height slack (which left too much space below the value and
    // between the two lines). `gap` is the exact space between header and value.
    ctx.textBaseline = 'alphabetic';
    ctx.font = `Bold ${headerSize}px ${fontface}`;
    const hM = ctx.measureText(header);
    const headerW = hM.width;
    const headerAscent = hM.actualBoundingBoxAscent || headerSize * 0.72;
    const headerDescent = hM.actualBoundingBoxDescent || 0;
    ctx.font = `Bold ${valueSize}px ${fontface}`;
    const vM = ctx.measureText(value);
    const valueW = hasValue ? vM.width : 0;
    const valueAscent = vM.actualBoundingBoxAscent || valueSize * 0.72;
    const valueDescent = vM.actualBoundingBoxDescent || valueSize * 0.2;
    const textWidth = Math.max(valueW, headerW);

    const spriteWidth = 2 * margin + textWidth + 2 * bt;
    const spriteHeight =
        2 * margin +
        headerAscent +
        headerDescent +
        (hasValue ? gap + valueAscent + valueDescent : 0) +
        2 * bt;

    canvas.width = Math.ceil(spriteWidth);
    canvas.height = Math.ceil(spriteHeight);

    ctx.fillStyle = cssRgba(label.backgroundColor ?? { r: 0, g: 0, b: 0, a: 1 });
    ctx.strokeStyle = cssRgba(label.borderColor ?? { r: 0, g: 0, b: 0, a: 1 });
    ctx.lineWidth = bt;
    roundRectPath(ctx, bt / 2, bt / 2, spriteWidth - bt, spriteHeight - bt, 6);
    ctx.fill();
    ctx.stroke();

    // Left-aligned, one under the other.
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const x = bt + margin;

    // header (green), then the value (normal text colour) when there is one
    ctx.font = `Bold ${headerSize}px ${fontface}`;
    ctx.fillStyle = cssRgba(label._verticalHeaderColor ?? { r: 255, g: 255, b: 255, a: 1 });
    ctx.fillText(header, x, bt + margin + headerAscent);
    if (hasValue) {
        ctx.font = `Bold ${valueSize}px ${fontface}`;
        ctx.fillStyle = cssRgba(label.textColor ?? { r: 255, g: 255, b: 255, a: 1 });
        ctx.fillText(value, x, bt + margin + headerAscent + headerDescent + gap + valueAscent);
    }

    const texture = new THREE.Texture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    // Dispose the texture this redraw replaces — VERTICAL labels are redrawn on
    // every marker move / selection / border restyle, so not freeing it leaks a
    // GPU texture each time.
    const prev = label.sprite.material.map;
    if (prev && prev !== texture && prev.dispose) prev.dispose();
    label.sprite.material.map = texture;
    label.texture = texture;
    label.sprite.scale.set(spriteWidth * 0.01, spriteHeight * 0.01, 1.0);
}

/** Compact string of a point, for the label-redraw signature. */
function v3sig(v: THREE.Vector3 | null): string {
    return v ? `${v.x},${v.y},${v.z}` : '·';
}

const START_COLOR = 0xffff00;
const END_COLOR = 0xffff00;
const P_PRIME_COLOR = 0xffffff;
const POINT_RADIUS = 0.04;
const P_PRIME_RADIUS = 0.06;
const HEMISPHERE_OPACITY = 0.22;
/** White P′ spheres/line — off until we need to debug/show representative points. */
const SHOW_P_PRIME_MARKERS = false;

export type SmoothedLabelMode = 'distance_slope' | 'distance_only' | 'slope_only';

export interface NeighborVizUpdate {
    /** Potree Measure object — labels are parented here so they survive new measurements. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    measurement?: any;
    startPos: THREE.Vector3 | null;
    endPos: THREE.Vector3 | null;
    startNeighbors?: THREE.Vector3[];
    endNeighbors?: THREE.Vector3[];
    startPPrime?: THREE.Vector3 | null;
    endPPrime?: THREE.Vector3 | null;
    /** When false, keep existing labels for this measurement (e.g. while dragging). */
    replaceLabels?: boolean;
    /** Which P′ labels to show (Distance&Slope vs Slope-only tool). */
    labelMode?: SmoothedLabelMode;
}

export class NeighborVisualization {
    private group: THREE.Group;
    private viewer: any;
    private hemisphereGeo: THREE.SphereGeometry;
    private pointSphereGeo: THREE.SphereGeometry;
    private pPrimeSphereGeo: THREE.SphereGeometry;
    private materials: THREE.Material[] = [];
    private geometries: THREE.BufferGeometry[] = [];
    // Labels are keyed by measurement so starting a new one does not wipe old labels.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private labels: { object: any; type: 'distance' | 'height' | 'slope'; measurement: any }[] = [];
    // Signature of the labels currently drawn per measurement (positions + P′ +
    // labelMode). Lets any redraw request skip the dispose+recreate when nothing
    // that affects the displayed value/text changed — e.g. a pure selection, which
    // re-shows the panel with identical geometry. Weak so removed measurements GC.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private labelSig = new WeakMap<object, string>();
    private onViewerUpdate: (() => void) | null = null;
    private onLabelClick: ((e: MouseEvent) => void) | null = null;
    // The measurement whose on-line labels are highlighted (white border) to show
    // it is selected. New labels read this; setSelectedMeasurement restyles existing.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private selectedMeasurement: any = null;

    constructor(viewer: any) {
        this.viewer = viewer;
        this.group = new THREE.Group();
        this.group.name = 'neighbor_validation_markers';
        // Scale marker geometry to the cloud's native units so the yellow dome
        // matches the actual search radius (which is also unit-scaled).
        const sceneRadius = hemisphereRadiusScene(viewer);
        const unitScale = sceneRadius / HEMISPHERE_RADIUS_FT;
        // thetaLength = π/2 → dome opens along +Y
        this.hemisphereGeo = new THREE.SphereGeometry(
            sceneRadius,
            24,
            12,
            0,
            Math.PI * 2,
            0,
            Math.PI / 2
        );
        this.pointSphereGeo = new THREE.SphereGeometry(POINT_RADIUS * unitScale, 8, 8);
        this.pPrimeSphereGeo = new THREE.SphereGeometry(P_PRIME_RADIUS * unitScale, 10, 10);
        viewer.scene.scene.add(this.group);

        // Match Potree MeasuringTool: keep label screen size stable while zooming.
        this.onViewerUpdate = () => this.updateLabelScales();
        viewer.addEventListener('update', this.onViewerUpdate);

        this.onLabelClick = (e: MouseEvent) => this.handleLabelClick(e);
        viewer.renderer?.domElement?.addEventListener('click', this.onLabelClick);
    }

    /** Clear only active hemispheres / neighbor dots (not completed measurement labels). */
    clearEphemeral() {
        for (const m of this.materials) {
            m.dispose();
        }
        this.materials = [];
        for (const g of this.geometries) {
            g.dispose();
        }
        this.geometries = [];
        while (this.group.children.length > 0) {
            this.group.remove(this.group.children[0]);
        }
    }

    // Free a label sprite's GPU resources. Every label owns a canvas texture (and a
    // SpriteMaterial); Potree replaced the texture on each redraw and never disposed
    // it, and removing a label without disposing leaked the last one too.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private disposeLabel(label: any) {
        const material = label?.sprite?.material;
        const map = material?.map;
        if (map?.dispose) map.dispose();
        if (material?.dispose) material.dispose();
        // TextSprite mirrors the texture on .texture; dispose it too if distinct.
        if (label?.texture?.dispose && label.texture !== map) label.texture.dispose();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeLabelsFor(measurement: any) {
        if (!measurement) return;
        const remaining = [];
        for (const entry of this.labels) {
            if (entry.measurement === measurement) {
                entry.object.parent?.remove(entry.object);
                this.disposeLabel(entry.object);
            } else {
                remaining.push(entry);
            }
        }
        this.labels = remaining;
        // Labels are gone, so the recorded signature no longer applies.
        this.labelSig.delete(measurement);
    }

    clearAllLabels() {
        for (const entry of this.labels) {
            entry.object.parent?.remove(entry.object);
            this.disposeLabel(entry.object);
        }
        this.labels = [];
        this.labelSig = new WeakMap();
    }

    // Whether this measurement currently has any on-line labels drawn — lets callers
    // skip a redraw when the label is already present and unchanged.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hasLabelsFor(measurement: any): boolean {
        return this.labels.some((e) => e.measurement === measurement);
    }

    // True when the labels for `measurement` must be (re)drawn for signature `sig`:
    // either the signature changed (value/geometry/tool differs) or the labels were
    // cleared elsewhere. False → the existing labels already match, reuse them and
    // skip the dispose+recreate. `sig === 'none'` means "no labels" (nothing drawn).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private needsLabelRedraw(measurement: any, sig: string): boolean {
        if (this.labelSig.get(measurement) !== sig) return true;
        return sig !== 'none' && !this.hasLabelsFor(measurement);
    }

    /** Clear ephemeral overlays and all smoothed labels (e.g. reset all measurements). */
    clear() {
        this.clearEphemeral();
        this.clearAllLabels();
    }

    // Highlight the selected measurement's on-line labels with a white border
    // (pass null to clear). Restyles existing labels and is remembered so labels
    // recreated by a later analysis keep the highlight.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSelectedMeasurement(measurement: any) {
        this.selectedMeasurement = measurement ?? null;
        for (const { object, measurement: m } of this.labels) {
            const sel = measurement != null && m === measurement;
            // setBorderColor redraws the label's canvas texture, so only touch labels
            // whose selected state actually flips — this used to redraw EVERY label on
            // every selection change (a big source of texture churn).
            if (object._selBorder === sel) continue;
            object._selBorder = sel;
            object.setBorderColor(
                sel ? { r: 255, g: 255, b: 255, a: 1.0 } : { r: 0, g: 0, b: 0, a: 1.0 }
            );
            // All labels transparent (see makeLabel) so lines never cover them;
            // selected-on-top is handled by moving the group last in the scene.
            object.material.alphaTest = 0.5;
            object.material.transparent = true;
            object.material.needsUpdate = true;
        }
    }

    dispose() {
        if (this.onViewerUpdate) {
            this.viewer.removeEventListener('update', this.onViewerUpdate);
            this.onViewerUpdate = null;
        }
        if (this.onLabelClick) {
            this.viewer.renderer?.domElement?.removeEventListener('click', this.onLabelClick);
            this.onLabelClick = null;
        }
        this.clear();
        this.hemisphereGeo.dispose();
        this.pointSphereGeo.dispose();
        this.pPrimeSphereGeo.dispose();
        this.viewer.scene.scene.remove(this.group);
    }

    private updateLabelScales() {
        if (this.labels.length === 0) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Potree = (window as any).Potree;
        const Utils = Potree?.Utils;
        if (!Utils?.projectedRadius) return;

        const camera = this.viewer.scene.getActiveCamera();
        const renderer = this.viewer.renderer;
        if (!camera || !renderer) return;

        const size = new THREE.Vector2();
        renderer.getSize(size);
        const worldPos = new THREE.Vector3();

        for (const { object: label } of this.labels) {
            label.getWorldPosition(worldPos);
            const distance = camera.position.distanceTo(worldPos);
            const pr = Utils.projectedRadius(1, camera, distance, size.x, size.y);
            if (!pr || !Number.isFinite(pr)) continue;
            // Match Potree MeasuringTool edge/slope label screen size.
            const scale = 70 / pr;
            label.scale.set(scale, scale, scale);
        }
    }

    private handleLabelClick(e: MouseEvent) {
        if (e.button !== 0) return;
        if (this.labels.length === 0) return;
        if (this.viewer._suppressLabelClick) return;

        const camera = this.viewer.scene.getActiveCamera();
        const dom = this.viewer.renderer?.domElement;
        if (!camera || !dom) return;

        const rect = dom.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const worldPos = new THREE.Vector3();
        const threshold = 50;

        let closest: { type: 'distance' | 'height' | 'slope'; dist: number; text: string } | null = null;

        for (const { object: label, type } of this.labels) {
            if (!label.visible) continue;
            label.getWorldPosition(worldPos);
            const projected = worldPos.clone().project(camera);
            const screenX = ((projected.x + 1) / 2) * dom.clientWidth;
            const screenY = ((-projected.y + 1) / 2) * dom.clientHeight;
            const dist = Math.hypot(mouseX - screenX, mouseY - screenY);
            if (!closest || dist < closest.dist) {
                closest = { type, dist, text: String(label.text ?? '') };
            }
        }

        if (!closest || closest.dist > threshold) return;

        const value = parseFloat(closest.text.replace(/,/g, ''));
        if (Number.isNaN(value)) return;

        navigator.clipboard.writeText(value.toString());
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const showCopySuccess = (window as any).showCopySuccess;
        if (typeof showCopySuccess === 'function') {
            const kind =
                closest.type === 'distance'
                    ? 'Distance'
                    : closest.type === 'height'
                      ? 'Height'
                      : 'Slope';
            showCopySuccess(`${kind} copied: ${value}`);
        }
        e.stopImmediatePropagation();
    }

    update({
        measurement = null,
        startPos,
        endPos,
        startNeighbors = [],
        endNeighbors = [],
        startPPrime = null,
        endPPrime = null,
        replaceLabels = true,
        labelMode = 'distance_slope',
    }: NeighborVizUpdate) {
        this.clearEphemeral();

        if (startPos && endPos && startPos.distanceTo(endPos) > 1e-3) {
            this.addHemisphere(startPos, endPos, START_COLOR);
            this.addHemisphere(endPos, startPos, END_COLOR);
        }

        this.addPoints(startNeighbors, START_COLOR);
        this.addPoints(endNeighbors, END_COLOR);

        // Representative points P′ — values always used for labels; markers optional.
        if (SHOW_P_PRIME_MARKERS) {
            if (startPPrime) {
                this.addPPrime(startPPrime);
            }
            if (endPPrime) {
                this.addPPrime(endPPrime);
            }
            if (startPPrime && endPPrime) {
                this.addPPrimeLine(startPPrime, endPPrime);
            }
        }

        if (replaceLabels && measurement) {
            const vertical = !!(startPos && endPos && isNearVertical(startPos, endPos));
            // Signature of what the labels will show. Same signature → identical
            // labels, so reuse them (skip the dispose+recreate). A state change on
            // re-adjustment (→ Move point, → Vertical, moved P′) changes the signature
            // and rebuilds; a pure re-show (selection) does not.
            let sig = 'none';
            if (vertical && startPos && endPos) {
                sig = `V|${v3sig(startPos)}|${v3sig(endPos)}|${labelMode}`;
            } else if (startPPrime && endPPrime && startPos && endPos) {
                sig = `S|${v3sig(startPos)}|${v3sig(endPos)}|${v3sig(startPPrime)}|${v3sig(endPPrime)}|${labelMode}`;
            }
            if (this.needsLabelRedraw(measurement, sig)) {
                this.removeLabelsFor(measurement);
                this.labelSig.set(measurement, sig);
                if (vertical && startPos && endPos) {
                    // Vertical (any tool): keep the tool's normal labels but add a green
                    // "VERTICAL" header; the slope value is voided to "—".
                    this.addPPrimeLineLabels(measurement, startPos, endPos, null, null, labelMode, true);
                } else if (startPPrime && endPPrime && startPos && endPos) {
                    // Values from P′; labels parented to the measurement so they persist.
                    this.addPPrimeLineLabels(
                        measurement,
                        startPos,
                        endPos,
                        startPPrime,
                        endPPrime,
                        labelMode
                    );
                }
                // sig === 'none' → no P′ / hidden: removeLabelsFor already cleared them.
            }
        }
    }

    private addHemisphere(center: THREE.Vector3, toward: THREE.Vector3, color: number) {
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: HEMISPHERE_OPACITY,
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide,
        });
        this.materials.push(material);

        const mesh = new THREE.Mesh(this.hemisphereGeo, material);
        mesh.position.copy(center);

        const dir = new THREE.Vector3().subVectors(toward, center);
        if (dir.lengthSq() >= 1e-12) {
            dir.normalize();
            mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        }

        mesh.renderOrder = 998;
        this.group.add(mesh);
    }

    private addPoints(points: THREE.Vector3[], color: number) {
        if (points.length === 0) return;

        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false,
        });
        this.materials.push(material);

        for (const p of points) {
            const sphere = new THREE.Mesh(this.pointSphereGeo, material);
            sphere.position.copy(p);
            sphere.renderOrder = 999;
            this.group.add(sphere);
        }
    }

    private addPPrime(position: THREE.Vector3) {
        const material = new THREE.MeshBasicMaterial({
            color: P_PRIME_COLOR,
            transparent: true,
            opacity: 0.95,
            depthTest: false,
            depthWrite: false,
        });
        this.materials.push(material);

        const sphere = new THREE.Mesh(this.pPrimeSphereGeo, material);
        sphere.position.copy(position);
        sphere.renderOrder = 1000;
        this.group.add(sphere);
    }

    private addPPrimeLine(a: THREE.Vector3, b: THREE.Vector3) {
        const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
        this.geometries.push(geometry);

        const material = new THREE.LineBasicMaterial({
            color: P_PRIME_COLOR,
            transparent: true,
            opacity: 0.85,
            depthTest: false,
            depthWrite: false,
        });
        this.materials.push(material);

        const line = new THREE.Line(geometry, material);
        line.renderOrder = 1000;
        this.group.add(line);
    }

    private addPPrimeLineLabels(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        measurement: any,
        lineA: THREE.Vector3,
        lineB: THREE.Vector3,
        pPrimeA: THREE.Vector3 | null,
        pPrimeB: THREE.Vector3 | null,
        labelMode: SmoothedLabelMode,
        // Vertical: keep the tool's normal label set, but add a green "VERTICAL"
        // header to each label and void the slope value to "—" (slope is
        // meaningless on a vertical face). Distance stays valid.
        vertical = false
    ) {
        // Potree is attached to window in this app.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Potree = (window as any).Potree;
        const TextSprite = Potree?.TextSprite;
        if (!TextSprite) return;

        // Slope comes from the smoothed representative points P′ (de-noised height),
        // but the DISTANCE label reads the raw clicks (lineA/lineB) so it always shows
        // the Standard distance — Smoothed never changes the reported distance.
        // Vertical → no valid slope, so skip the P′ slope computation entirely.
        const slopePercent =
            !vertical && pPrimeA && pPrimeB
                ? computeDistanceAndSlope(
                      { x: pPrimeA.x, y: pPrimeA.y, z: pPrimeA.z },
                      { x: pPrimeB.x, y: pPrimeB.y, z: pPrimeB.z }
                  ).slopePercent
                : 0;
        const { distance } = computeDistanceAndSlope(
            { x: lineA.x, y: lineA.y, z: lineA.z },
            { x: lineB.x, y: lineB.y, z: lineB.z }
        );

        // Match Potree's Standard label placement (Measure.update): both labels at
        // the line midpoint; when distance + slope are both shown, nudge the distance
        // label down and the slope label up in world-z so they don't overlap. Same
        // offsets and clamps Potree uses, so Smoothed labels land where Standard's do.
        const mid = lineA.clone().add(lineB).multiplyScalar(0.5);
        const lineLength = lineA.distanceTo(lineB);
        // Vertical drops the slope label entirely, so a vertical Distance & Slope
        // shows only the distance label — no need to nudge it off the midpoint.
        const bothShown = labelMode === 'distance_slope' && !vertical;
        const distancePos = mid.clone();
        const slopePos = mid.clone();
        if (bothShown) {
            distancePos.z -= Math.min(Math.max(lineLength * 0.03, 0.3), 2.0);
            slopePos.z += Math.min(Math.max(lineLength * 0.03, 0.4), 2.5);
        }

        const makeLabel = (
            text: string,
            position: THREE.Vector3,
            renderOrder: number,
            type: 'distance' | 'height' | 'slope'
        ) => {
            const label = new TextSprite(text);
            // Same canvas size as Potree measure labels; parent scale keeps
            // screen size stable while zooming.
            label.fontsize = 16;
            // Thinner than Potree's default (4) so the selected white frame is a fine
            // outline. Set before the color setters, which redraw using it.
            label.borderThickness = 2;
            // Vertical → render as a two-line label with a green "VERTICAL" header.
            if (vertical) {
                label._verticalHeader = 'VERTICAL';
                label._verticalHeaderColor = VERTICAL_HEADER_COLOR;
                label.update = () => drawTwoLineLabel(label);
            }
            // Match Potree Standard colours: the slope label is teal (same rgb Potree
            // uses for its slope label), distance/height stay white. Vertical labels
            // are all white (the value / "—" under the green header).
            label.setTextColor(
                type === 'slope' && !vertical
                    ? { r: 86, g: 196, b: 198, a: 1.0 }
                    : { r: 255, g: 255, b: 255, a: 1.0 }
            );
            // White border when this measurement is selected, black (invisible on the
            // black box) otherwise. Record the state so setSelectedMeasurement can skip
            // labels whose border doesn't change (avoids a redundant redraw).
            const sel = measurement === this.selectedMeasurement;
            label._selBorder = sel;
            label.setBorderColor(
                sel ? { r: 255, g: 255, b: 255, a: 1.0 } : { r: 0, g: 0, b: 0, a: 1.0 }
            );
            label.setBackgroundColor({ r: 0, g: 0, b: 0, a: 1.0 });
            label.material.depthTest = false;
            label.position.copy(position);
            label.renderOrder = renderOrder;
            if (label.sprite) {
                label.sprite.renderOrder = renderOrder;
            }
            // Potree runs the renderer with sortObjects=false, so renderOrder/z are
            // ignored — draw order inside a pass is scene-graph traversal order. All
            // labels are transparent so they draw in the transparent pass, after the
            // opaque lines/markers (labels never covered by lines); alphaTest keeps
            // the corners rounded. The selected group is moved last in the scene
            // (see reorderMeasurementLast in Sidebar) so its labels draw last → top.
            label.material.alphaTest = 0.5;
            label.material.transparent = true;
            label.material.needsUpdate = true;
            // Parent to the measure (like Potree edgeLabels) so labels stay with it.
            measurement.add(label);
            this.labels.push({ object: label, type, measurement });
        };

        // Smoothed lengths are shown in inches. Use the viewer's OWN native→display
        // ratio (lengthUnitDisplay / lengthUnit, exactly what the Measurement Review
        // panel uses) so the on-line label matches the panel's Standard/Smoothed to
        // the last digit. Fall back to the fixed factor only if those aren't set.
        const nativeCode = this.viewer?.lengthUnit?.code;
        const nativePerM = this.viewer?.lengthUnit?.unitspermeter;
        const displayPerM = this.viewer?.lengthUnitDisplay?.unitspermeter;
        const inchesPerNative =
            nativePerM && displayPerM
                ? displayPerM / nativePerM
                : nativeCode === 'm'
                  ? 39.3701
                  : 12;
        const fmtIn = (native: number) => `${(native * inchesPerNative).toFixed(2)} ″`;

        // Per tool: Slope shows slope% only, Distance shows distance only,
        // Distance & Slope shows both.
        // Vertical → slope is meaningless, so:
        //  - Slope-only tool: keep a label but show just the "VERTICAL" header (no value).
        //  - Distance & Slope: drop the slope label; the distance label carries VERTICAL.
        if (!vertical && labelMode !== 'distance_only') {
            makeLabel(`${slopePercent.toFixed(2)}%`, slopePos, 4001, 'slope');
        } else if (vertical && labelMode === 'slope_only') {
            makeLabel('', slopePos, 4001, 'slope');
        }
        if (labelMode !== 'slope_only') {
            makeLabel(fmtIn(distance), distancePos, 4002, 'distance');
        }
        this.updateLabelScales();
    }

    // Public entry so Standard measurements (which don't run the smoothed update
    // path) can show VERTICAL labels too. Clears this measurement's prior labels,
    // then draws the tool's normal label set with a green "VERTICAL" header (slope
    // voided to "—"). labelMode picks which labels the tool shows.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    drawVerticalLabel(
        measurement: any,
        lineA: THREE.Vector3,
        lineB: THREE.Vector3,
        labelMode: SmoothedLabelMode
    ) {
        // Same signature scheme as the smoothed update() path: reuse the existing
        // label on a pure re-show (selection), rebuild only when the points/tool
        // change. Format matches the vertical branch there so both stay consistent.
        const sig = `V|${v3sig(lineA)}|${v3sig(lineB)}|${labelMode}`;
        if (!this.needsLabelRedraw(measurement, sig)) return;
        this.removeLabelsFor(measurement);
        this.labelSig.set(measurement, sig);
        this.addPPrimeLineLabels(measurement, lineA, lineB, null, null, labelMode, true);
    }
}
